import { toggleDataGridRowDetails } from '../data-grid/mobile-expand.js';

export default {
    isMobileViewport() {
        return this.useDivGrid && window.matchMedia('(max-width: 767px)').matches;
    },

    getExpandContentRequestKey(key) {
        const tableName = key.type === 'pledge' && key.tableName ? key.tableName : '';
        return `expand-content-${key.type}-${tableName}-${key.id}`;
    },

    isManagerPledgeExpand(key) {
        return key.type === 'pledge' && this.portal !== 'member';
    },

    wantsRemoteExpandContent(key) {
        return key.type !== 'pledge' || this.isManagerPledgeExpand(key);
    },

    shouldLoadExpandContent($placeholder) {
        return !$placeholder.length || $placeholder.hasClass('payment-details-loading') || $.trim($placeholder.text()) === '';
    },

    isRowExpandActive($row, key) {
        if (!$row.hasClass('expanded')) {
            return false;
        }
        const $detailRow = this.getDetailRowDivByRowKey(key);
        return $detailRow.length > 0 && $detailRow.is(':visible');
    },

    fetchExpandContentHtml(key) {
        if (this.isManagerPledgeExpand(key)) {
            return this.fetchPledgeExpandContent(key.id, key.tableName);
        }
        return this.fetchMemberExpandContent(key);
    },

    loadExpandContent($detailRow, $row, key, animDone) {
        const $placeholder = $detailRow.find('[data-expand-content]');
        if (!$placeholder.length || !this.shouldLoadExpandContent($placeholder)) {
            return;
        }

        const requestKey = this.getExpandContentRequestKey(key);
        this.abortRequest(requestKey);

        let jqXHR;
        try {
            jqXHR = this.fetchExpandContentHtml(key);
        } catch (err) {
            console.error('loadExpandContent:', err);
            Promise.resolve(animDone).then(() => {
                if (this.isRowExpandActive($row, key)) {
                    this.setExpandContentError($placeholder, 'Failed to load content.');
                }
            });
            return;
        }

        this.activeRequests[requestKey] = jqXHR;
        const isPledge = this.isManagerPledgeExpand(key);

        Promise.all([animDone, Promise.resolve(jqXHR)]).then(([, html]) => {
            if (this.activeRequests[requestKey] !== jqXHR) {
                return;
            }
            delete this.activeRequests[requestKey];
            if (!this.isRowExpandActive($row, key)) {
                return;
            }

            this.withDetailHeightTransition($detailRow, () => {
                this.setExpandContentFromServer($placeholder, html, isPledge ? 'pledge' : 'member');
            }, () => {
                if (!this.isRowExpandActive($row, key)) {
                    return;
                }
                if (isPledge) {
                    this.loadPaymentDetails(key.id, $row.data('table-name'));
                    return;
                }
                this.loadMemberDetails($row);
                this.loadMemberExpandPanelFragments($row);
            });
        }).catch(() => {
            if (this.activeRequests[requestKey] !== jqXHR) {
                return;
            }
            delete this.activeRequests[requestKey];
            if (this.isRowExpandActive($row, key)) {
                this.setExpandContentError($placeholder, 'Failed to load content.');
            }
        });
    },

    setExpandContentError($placeholder, message) {
        $placeholder
            .removeClass('payment-details-loading')
            .empty()
            .append($('<div>').addClass('text-danger').text(message));
    },

    setExpandContentFromServer($placeholder, html, keyType) {
        const $content = $(html);
        const writeCapability = keyType === 'member' ? 'members' : 'pledges';
        if (!this.canEditCapability(writeCapability)) {
            $content.find('[data-manager-write-option="1"]').hide();
        }
        $placeholder.removeClass('payment-details-loading').empty().append($content);
    },

    withDetailHeightTransition($detailRow, mutateDom, onComplete) {
        const finish = typeof onComplete === 'function' ? onComplete : () => {};
        const el = $detailRow && $detailRow[0];
        if (!el || !$detailRow.is(':visible')) {
            mutateDom();
            finish();
            return;
        }

        const fromHeight = el.getBoundingClientRect().height;
        el.style.height = `${fromHeight}px`;
        el.style.overflow = 'hidden';
        mutateDom();
        const toHeight = el.scrollHeight;

        if (Math.abs(toHeight - fromHeight) < 2) {
            el.style.height = '';
            el.style.overflow = '';
            finish();
            return;
        }

        requestAnimationFrame(() => {
            el.style.transition = `height ${this.ANIMATION_DURATION}ms ease`;
            el.style.height = `${toHeight}px`;
        });

        let settled = false;
        const clearInlineHeight = () => {
            if (settled) {
                return;
            }
            settled = true;
            el.style.transition = '';
            el.style.height = '';
            el.style.overflow = '';
            el.removeEventListener('transitionend', onTransitionEnd);
            finish();
        };
        const onTransitionEnd = (event) => {
            if (event.target !== el || event.propertyName !== 'height') {
                return;
            }
            clearInlineHeight();
        };
        el.addEventListener('transitionend', onTransitionEnd);
        setTimeout(clearInlineHeight, this.ANIMATION_DURATION + 80);
    },

    clearDetailHeightStyles($detailRow) {
        if (!$detailRow.length) {
            return;
        }
        const el = $detailRow[0];
        el.style.transition = '';
        el.style.height = '';
        el.style.overflow = '';
    },

    openRowByGridRowId(rowId) {
        if (!rowId) {
            return false;
        }
        const item = document.querySelector('[data-row-id="' + CSS.escape(String(rowId)) + '"]');
        if (!item) {
            return false;
        }
        if (this.isMobileViewport()) {
            if (item.classList.contains('details-expanded')) {
                return true;
            }
            toggleDataGridRowDetails(rowId, document);
            return item.classList.contains('details-expanded');
        }
        const $row = $(item).find('.clickable-row').first();
        if (!$row.length) {
            return false;
        }
        if (this.isExpanded($row)) {
            return true;
        }
        if (this.isToggling) {
            return false;
        }
        this.toggleRowExpansion($row);
        return true;
    },

    attachClickHandler() {
        const expander = this;
        const delegateRoot = this.getGridDelegationRoot();
        const $root = $(delegateRoot);
        $root.off('click.pledgeRowExpander', '.clickable-row');
        $root.on('click.pledgeRowExpander', '.clickable-row', function (e) {
            if (expander.shouldIgnoreClick(e)) {
                return;
            }
            if (expander.useDivGrid && window.matchMedia('(max-width: 767px)').matches) {
                return;
            }
            e.preventDefault();
            expander.toggleRowExpansion($(this));
        });
    },

    shouldIgnoreClick(e) {
        return $(e.target).closest('input[type="checkbox"], .data-grid-cell-check, .dropdown, a').length > 0 || this.isToggling;
    },

    ensureDetailRowPositioned($row, key) {
        const detailRowId = this.getDetailRowId(key);
        const detailEl = detailRowId ? document.getElementById(detailRowId) : null;
        if (!detailEl) {
            return false;
        }
        if ($row[0].nextElementSibling === detailEl) {
            return true;
        }
        const prefix = this.getSpacerPrefix(key);
        const $detailRow = $(detailEl);
        const topEl = document.getElementById('spacer-top-' + prefix);
        const bottomEl = document.getElementById('spacer-bottom-' + prefix);
        const $topSpacer = topEl ? $(topEl) : $();
        const $bottomSpacer = bottomEl ? $(bottomEl) : $();
        $topSpacer.detach();
        $detailRow.detach();
        $bottomSpacer.detach();
        $row.before($topSpacer);
        $row.after($detailRow);
        $detailRow.after($bottomSpacer);
        return true;
    },

    detailRowExists(key) {
        const id = this.getDetailRowId(key);
        return !!(id && document.getElementById(id));
    },

    toggleRowExpansion($row) {
        if (this.isToggling) return;
        const key = this.getRowKey($row);
        if (!key) {
            throw new Error('toggleRowExpansion: row key is required');
        }
        this.isToggling = true;

        if (!this.detailRowExists(key)) {
            this.initializeAndExpand($row, key);
            return;
        }

        const $detailRow = this.getDetailRowDivByRowKey(key);
        if (!$detailRow.length) {
            this.resetToggleFlag();
            return;
        }

        this.ensureDetailRowPositioned($row, key);

        const isCollapsing = $detailRow.is(':visible');
        if (isCollapsing) {
            this.isToggling = false;
        }

        this.toggleDetailRow($row, $detailRow, key);

        if (!isCollapsing) {
            this.resetToggleFlag();
        }
    },

    /** @param {*} key @returns {JQuery} */
    getDetailRowDivByRowKey(key) {
        const id = this.getDetailRowId(key);
        const el = id ? document.getElementById(id) : null;
        return el ? $(el) : $();
    },

    getPaymentDetailsDivById(pledgeId, tableName) {
        const id = this.getPaymentDetailsId(pledgeId, tableName);
        const el = id ? document.getElementById(id) : null;
        return el ? $(el) : $();
    },

    initializeAndExpand($row, key) {
        const info = this.getUserInfoFromRow($row);
        const detailRows = this.createDetailRow(key, info, $row);
        const prefix = this.getSpacerPrefix(key);
        $row.before(detailRows.topSpacer);
        $row.after(detailRows.detailRow);
        detailRows.detailRow.after(detailRows.bottomSpacer);

        requestAnimationFrame(() => {
            const $detailRow = this.getDetailRowDivByRowKey(key);
            const topEl = document.getElementById('spacer-top-' + prefix);
            const bottomEl = document.getElementById('spacer-bottom-' + prefix);
            const $topSpacer = topEl ? $(topEl) : $();
            const $bottomSpacer = bottomEl ? $(bottomEl) : $();

            if ($detailRow.length && $row.length) {
                this.collapseAllRowsExcept($row);
                this.ensureDetailRowPositioned($row, key);
                $topSpacer.show();
                $bottomSpacer.show();
                this.openDetailRow($row, $detailRow, key);
            }

            this.resetToggleFlag();
        });
    },

    toggleDetailRow($row, $detailRow, key) {
        const prefix = this.getSpacerPrefix(key);
        const topEl = document.getElementById('spacer-top-' + prefix);
        const bottomEl = document.getElementById('spacer-bottom-' + prefix);
        const $topSpacer = topEl ? $(topEl) : $();
        const $bottomSpacer = bottomEl ? $(bottomEl) : $();

        if ($detailRow.is(':visible')) {
            this.abortExpandRequestsForKey(key);
            $topSpacer.hide();
            $bottomSpacer.hide();
            this.collapseRow($row, $detailRow);
            return;
        }

        this.collapseAllRowsExcept($row);
        $topSpacer.show();
        $bottomSpacer.show();
        this.openDetailRow($row, $detailRow, key);
    },

    openDetailRow($row, $detailRow, key) {
        const animDone = this.expandRow($row, $detailRow);
        const $expandContent = $detailRow.find('[data-expand-content]');

        if (this.wantsRemoteExpandContent(key) && this.shouldLoadExpandContent($expandContent)) {
            this.loadExpandContent($detailRow, $row, key, animDone);
            return;
        }

        animDone.then(() => {
            if (!this.isRowExpandActive($row, key)) {
                return;
            }
            this.loadSecondaryExpandPanels($row, $detailRow, key);
        });
    },

    loadSecondaryExpandPanels($row, $detailRow, key) {
        if (this.isManagerPledgeExpand(key)) {
            const tableName = key.tableName || $row.data('table-name');
            const $paymentDetails = this.getPaymentDetailsDivById(key.id, tableName);
            if (this.shouldLoadPaymentDetails($paymentDetails)) {
                this.loadPaymentDetails(key.id, tableName);
            }
            return;
        }
        if (key.type === 'pledge') {
            return;
        }
        const $memberDetails = $detailRow.find(`#member-details-${key.id}`);
        if (this.shouldLoadMemberDetails($memberDetails)) {
            this.loadMemberDetails($row);
        }
        if (this.shouldLoadMemberExpandPanels($row, key)) {
            this.loadMemberExpandPanelFragments($row);
        }
    },

    shouldLoadPaymentDetails($paymentDetails) {
        if (!$paymentDetails.length) return false;
        return $paymentDetails.hasClass('payment-details-loading') || $.trim($paymentDetails.text()) === '';
    },

    shouldLoadMemberDetails($container) {
        return !$container.length || $container.hasClass('payment-details-loading') || $.trim($container.text()) === '';
    },

    shouldLoadMemberExpandPanels($row, key) {
        const $pledges = this.getDetailContainer($row, key, 'recent-pledges');
        const $relationships = this.getDetailContainer($row, key, 'member-relationships');
        const $miSheberachCard = this.getDetailContainer($row, key, 'member-mi-sheberach-card');
        const needsLoad = ($container) =>
            $container.length > 0 &&
            ($container.hasClass('payment-details-loading') || $.trim($container.text()) === '');
        return needsLoad($pledges) || needsLoad($relationships) || needsLoad($miSheberachCard);
    },

    resetToggleFlag() {
        setTimeout(() => {
            this.isToggling = false;
        }, this.TOGGLE_DEBOUNCE);
    },

    expandRow($row, $detailRow) {
        return new Promise((resolve) => {
            $detailRow.slideDown(this.ANIMATION_DURATION, resolve);
            $row.addClass('expanded');
            this.emitRowExpandChange($row, true);
        });
    },

    collapseRow($row, $detailRow, onComplete) {
        this.clearDetailHeightStyles($detailRow);
        $detailRow.slideUp(this.ANIMATION_DURATION, () => {
            $detailRow.hide();
            $row.removeClass('expanded');
            this.emitRowExpandChange($row, false);
            if (typeof onComplete === 'function') {
                onComplete();
            }
        });
    },

    emitRowExpandChange($row, expanded) {
        const grid = document.getElementById(this.gridId);
        if (!grid) {
            return;
        }
        const item = $row.closest('.data-grid-item')[0];
        const rowId = item ? item.getAttribute('data-row-id') : '';
        if (!rowId) {
            return;
        }
        grid.dispatchEvent(new CustomEvent('data-grid-row-expand-change', {
            bubbles: true,
            detail: { rowId: rowId, expanded: !!expanded, gridId: grid.id },
        }));
    },

    /** @param {JQuery} $row @param {*} key @returns {void} */
    collapse($row, key) {
        this.collapseSingleRow($row, key);
    },

    /** @param {JQuery} $row @returns {void} */
    collapseAllRowsExcept($row) {
        const expander = this;
        $('.clickable-row.expanded').each(function () {
            const $other = $(this);
            if ($other[0] === $row[0]) {
                return;
            }
            const key = expander.getRowKey($other);
            if (key) {
                expander.collapseRowInstant($other, key);
            }
        });
    },

    abortExpandRequestsForKey(key) {
        const paymentKey = key.type === 'pledge' && key.tableName
            ? `payment-${key.tableName}-${key.id}`
            : `payment-${key.id}`;
        this.abortRequest(paymentKey);
        this.abortRequest(this.getExpandContentRequestKey(key));
        if (key.type === 'member') {
            this.abortRequest(`recent-${key.id}`);
            this.abortRequest(`member-${key.id}`);
        }
    },

    collapseRowInstant($row, key) {
        this.abortExpandRequestsForKey(key);
        const prefix = this.getSpacerPrefix(key);
        const $detailRow = this.getDetailRowDivByRowKey(key);
        const topEl = document.getElementById('spacer-top-' + prefix);
        const bottomEl = document.getElementById('spacer-bottom-' + prefix);
        if (topEl) {
            topEl.style.display = 'none';
        }
        if (bottomEl) {
            bottomEl.style.display = 'none';
        }
        this.clearDetailHeightStyles($detailRow);
        if ($detailRow.length) {
            $detailRow.stop(true, true).hide();
        }
        $row.removeClass('expanded');
        this.emitRowExpandChange($row, false);
        this.clearRowBorders($row);
    },

    collapseAllRows() {
        const expander = this;
        $('.clickable-row.expanded').each(function () {
            const $row = $(this);
            const key = expander.getRowKey($row);
            if (key) expander.collapseSingleRow($row, key);
        });
    },

    collapseSingleRow($row, key) {
        this.abortExpandRequestsForKey(key);
        const prefix = this.getSpacerPrefix(key);
        const $detailRow = this.getDetailRowDivByRowKey(key);
        const topEl = document.getElementById('spacer-top-' + prefix);
        const bottomEl = document.getElementById('spacer-bottom-' + prefix);
        const $topSpacer = topEl ? $(topEl) : $();
        const $bottomSpacer = bottomEl ? $(bottomEl) : $();

        if ($topSpacer.length) {
            $topSpacer.hide();
        }
        if ($bottomSpacer.length) {
            $bottomSpacer.hide();
        }
        if ($detailRow.length) {
            this.collapseRow($row, $detailRow, () => {
                this.clearRowBorders($row);
            });
        } else {
            $row.removeClass('expanded');
            this.clearRowBorders($row);
        }
    },

    clearRowBorders($row) {
        $row.find('td').css({
            'border-top': '',
            'border-left': '',
            'border-right': '',
            'border-bottom': '',
            'border-radius': ''
        });
    },
};
